import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Input,
  InputNumber,
  message,
  Result,
  Row,
  Segmented,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  CloudUploadOutlined,
  CodeOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { getTextContentDetail, saveTextContent, TextContentDetail, uploadHtmlContent } from '../../helpers/file';
import { copyToClipboard } from '../../helpers/clipboard';
import { useTranslation } from '../../hooks/useTranslation';
import { buildEditedHtmlFileName, isValidHtmlFileName } from '../../utils/htmlFileName';
import { isHtmlUploadRecord } from '../../utils/htmlRecord';
import { showUploadErrorModal } from '../../utils/uploadErrorModal';
import './index.css';

const EDITOR_SELECTED_CLASS = 'img-cloud-visual-editor-selected';
const EDITOR_HOVER_CLASS = 'img-cloud-visual-editor-hover';
const EDITOR_CONTENTEDITABLE_MARK = 'data-img-cloud-editor-contenteditable';
const GOOGLE_FONTS_CSS2_URL = 'https://fonts.googleapis.com/css2';
const TEXT_EDIT_MAX_BYTES = 2 * 1024 * 1024;
const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
const NON_TEXT_TAGS = new Set(['html', 'head', 'body', 'script', 'style', 'iframe', 'object', 'embed']);

interface SelectedElementFields {
  tagName: string;
  path: string;
  textContent: string;
  id: string;
  className: string;
  title: string;
  href: string;
  src: string;
  alt: string;
  style: string;
  textColor: string;
  backgroundColor: string;
  fontSize: number | null;
  textAlign: string;
  width: number | null;
  height: number | null;
  borderRadius: number | null;
  padding: number | null;
  margin: number | null;
  borderWidth: number | null;
  borderColor: string;
  opacity: number | null;
  canEditText: boolean;
  canMutate: boolean;
}

const EMPTY_SELECTED_FIELDS: SelectedElementFields = {
  tagName: '',
  path: '',
  textContent: '',
  id: '',
  className: '',
  title: '',
  href: '',
  src: '',
  alt: '',
  style: '',
  textColor: '',
  backgroundColor: '',
  fontSize: null,
  textAlign: '',
  width: null,
  height: null,
  borderRadius: null,
  padding: null,
  margin: null,
  borderWidth: null,
  borderColor: '',
  opacity: null,
  canEditText: false,
  canMutate: false,
};

const getHtmlByteSize = (html: string) => new Blob([html]).size;

const normalizeHexColor = (value = '', fallback = '#000000') => {
  const trimmed = value.trim();
  return /^#[0-9a-f]{6}$/i.test(trimmed) ? trimmed : fallback;
};

const readPixelStyle = (element: HTMLElement, propertyName: string) => {
  const value = element.style.getPropertyValue(propertyName).trim();
  if (!value) {
    return null;
  }
  const match = value.match(/^(-?\d+(?:\.\d+)?)px$/i);
  return match ? Number(match[1]) : null;
};

const readNumberStyle = (element: HTMLElement, propertyName: string) => {
  const value = element.style.getPropertyValue(propertyName).trim();
  if (!value) {
    return null;
  }
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const escapeCssClass = (value: string) => {
  return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
};

const serializeDoctype = (doctype: DocumentType | null) => {
  if (!doctype) {
    return '<!DOCTYPE html>';
  }

  const publicId = doctype.publicId ? ` PUBLIC "${doctype.publicId}"` : '';
  const systemPrefix = !doctype.publicId && doctype.systemId ? ' SYSTEM' : '';
  const systemId = doctype.systemId ? ` "${doctype.systemId}"` : '';
  return `<!DOCTYPE ${doctype.name}${publicId}${systemPrefix}${systemId}>`;
};

const cleanEditorMarkers = (root: ParentNode) => {
  const cleanElement = (element: HTMLElement) => {
    element.classList.remove(EDITOR_SELECTED_CLASS, EDITOR_HOVER_CLASS);
    if (element.getAttribute(EDITOR_CONTENTEDITABLE_MARK) === 'true') {
      element.removeAttribute('contenteditable');
    }
    element.removeAttribute(EDITOR_CONTENTEDITABLE_MARK);
  };

  if ((root as Node).nodeType === Node.ELEMENT_NODE) {
    cleanElement(root as HTMLElement);
  }

  root.querySelectorAll('#img-cloud-visual-editor-style').forEach((node) => node.remove());
  root.querySelectorAll(`.${escapeCssClass(EDITOR_SELECTED_CLASS)}, .${escapeCssClass(EDITOR_HOVER_CLASS)}, [${EDITOR_CONTENTEDITABLE_MARK}]`).forEach((node) => {
    const element = node as HTMLElement;
    cleanElement(element);
  });
};

const serializeDocument = (doc: Document) => {
  if (!doc.documentElement) {
    return '<!DOCTYPE html>\n<html><head></head><body></body></html>';
  }

  const clonedDocumentElement = doc.documentElement.cloneNode(true) as HTMLElement;
  cleanEditorMarkers(clonedDocumentElement);
  return `${serializeDoctype(doc.doctype)}\n${clonedDocumentElement.outerHTML}`;
};

const getUserClassName = (element: HTMLElement) => {
  const classNames = Array.from(element.classList).filter((className) => (
    className !== EDITOR_SELECTED_CLASS && className !== EDITOR_HOVER_CLASS
  ));
  return classNames.join(' ');
};

const canEditTextForElement = (element: HTMLElement) => {
  const tagName = element.tagName.toLowerCase();
  return !VOID_TAGS.has(tagName) && !NON_TEXT_TAGS.has(tagName);
};

const canMutateElement = (element: HTMLElement) => {
  const tagName = element.tagName.toLowerCase();
  return !['html', 'head', 'body'].includes(tagName);
};

const describeElement = (element: HTMLElement) => {
  const parts: string[] = [];
  let current: HTMLElement | null = element;

  while (current && current.tagName && parts.length < 5) {
    const tagName = current.tagName.toLowerCase();
    const id = current.id ? `#${current.id}` : '';
    const userClassName = getUserClassName(current).split(/\s+/).filter(Boolean).slice(0, 2)
      .map((className) => `.${className}`)
      .join('');
    parts.unshift(`${tagName}${id}${userClassName}`);
    current = current.parentElement;
  }

  return parts.join(' > ');
};

const readElementFields = (element: HTMLElement): SelectedElementFields => {
  const tagName = element.tagName.toLowerCase();
  const canEditText = canEditTextForElement(element);
  return {
    tagName,
    path: describeElement(element),
    textContent: canEditText ? (element.textContent || '') : '',
    id: element.id || '',
    className: getUserClassName(element),
    title: element.getAttribute('title') || '',
    href: element.getAttribute('href') || '',
    src: element.getAttribute('src') || '',
    alt: element.getAttribute('alt') || '',
    style: element.getAttribute('style') || '',
    textColor: element.style.getPropertyValue('color').trim(),
    backgroundColor: element.style.getPropertyValue('background-color').trim(),
    fontSize: readPixelStyle(element, 'font-size'),
    textAlign: element.style.getPropertyValue('text-align').trim(),
    width: readPixelStyle(element, 'width'),
    height: readPixelStyle(element, 'height'),
    borderRadius: readPixelStyle(element, 'border-radius'),
    padding: readPixelStyle(element, 'padding'),
    margin: readPixelStyle(element, 'margin'),
    borderWidth: readPixelStyle(element, 'border-width'),
    borderColor: element.style.getPropertyValue('border-color').trim(),
    opacity: readNumberStyle(element, 'opacity'),
    canEditText,
    canMutate: canMutateElement(element),
  };
};

const removeContentEditableMarker = (element: HTMLElement) => {
  if (element.getAttribute(EDITOR_CONTENTEDITABLE_MARK) === 'true') {
    element.removeAttribute('contenteditable');
  }
  element.removeAttribute(EDITOR_CONTENTEDITABLE_MARK);
};

const prepareElementForInlineEditing = (element: HTMLElement) => {
  if (!canEditTextForElement(element)) {
    return;
  }
  if (!element.hasAttribute('contenteditable')) {
    element.setAttribute(EDITOR_CONTENTEDITABLE_MARK, 'true');
    element.setAttribute('contenteditable', 'true');
  }
};

const setElementAttribute = (element: HTMLElement, attributeName: string, value: string) => {
  const nextValue = value.trim();
  if (nextValue) {
    element.setAttribute(attributeName, value);
  } else {
    element.removeAttribute(attributeName);
  }
};

const getOriginalUrl = (detail: TextContentDetail | null) => detail?.url || detail?.rawUrl || '';

const WebHostingEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tRef = useRef(t);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const selectedElementRef = useRef<HTMLElement | null>(null);
  const cleanupPreviewListenersRef = useRef<() => void>(() => undefined);

  const [detail, setDetail] = useState<TextContentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [sourceCode, setSourceCode] = useState('');
  const [sourceDirty, setSourceDirty] = useState(false);
  const [selectedFields, setSelectedFields] = useState<SelectedElementFields>(EMPTY_SELECTED_FIELDS);
  const [uploadFileName, setUploadFileName] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newUploadUrl, setNewUploadUrl] = useState('');

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const hasGoogleFontsResource = useMemo(() => sourceCode.includes(GOOGLE_FONTS_CSS2_URL), [sourceCode]);
  const htmlSize = useMemo(() => getHtmlByteSize(sourceCode), [sourceCode]);
  const isOverwriteTooLarge = htmlSize > TEXT_EDIT_MAX_BYTES;

  const clearSelectedElement = useCallback(() => {
    const selectedElement = selectedElementRef.current;
    if (selectedElement) {
      selectedElement.classList.remove(EDITOR_SELECTED_CLASS);
      removeContentEditableMarker(selectedElement);
    }
    selectedElementRef.current = null;
    setSelectedFields(EMPTY_SELECTED_FIELDS);
  }, []);

  const getPreviewDocument = useCallback(() => iframeRef.current?.contentDocument || null, []);

  const syncSourceFromPreview = useCallback(() => {
    const doc = getPreviewDocument();
    if (!doc?.documentElement) {
      return '';
    }
    const nextSourceCode = serializeDocument(doc);
    setSourceCode(nextSourceCode);
    setSourceDirty(false);
    return nextSourceCode;
  }, [getPreviewDocument]);

  const selectElement = useCallback((element: HTMLElement) => {
    const previousElement = selectedElementRef.current;
    if (previousElement && previousElement !== element) {
      previousElement.classList.remove(EDITOR_SELECTED_CLASS);
      removeContentEditableMarker(previousElement);
    }

    selectedElementRef.current = element;
    element.classList.remove(EDITOR_HOVER_CLASS);
    element.classList.add(EDITOR_SELECTED_CLASS);
    prepareElementForInlineEditing(element);
    if (canEditTextForElement(element)) {
      element.focus({ preventScroll: true });
    }
    setSelectedFields(readElementFields(element));
  }, []);

  const injectPreviewStyles = useCallback((doc: Document) => {
    if (doc.getElementById('img-cloud-visual-editor-style')) {
      return;
    }

    const style = doc.createElement('style');
    style.id = 'img-cloud-visual-editor-style';
    style.textContent = `
      .${EDITOR_HOVER_CLASS} { outline: 2px dashed #faad14 !important; outline-offset: 2px !important; cursor: crosshair !important; }
      .${EDITOR_SELECTED_CLASS} { outline: 3px solid #1677ff !important; outline-offset: 2px !important; box-shadow: 0 0 0 4px rgba(22, 119, 255, 0.16) !important; }
      [${EDITOR_CONTENTEDITABLE_MARK}="true"] { cursor: text !important; }
    `;
    (doc.head || doc.documentElement).appendChild(style);
  }, []);

  const handlePreviewLoad = useCallback(() => {
    cleanupPreviewListenersRef.current?.();
    const doc = getPreviewDocument();
    if (!doc?.documentElement) {
      return;
    }

    injectPreviewStyles(doc);
    clearSelectedElement();

    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || !target.tagName) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      selectElement(target);
    };

    const handleMouseOver = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || !target.tagName || target === selectedElementRef.current) {
        return;
      }
      target.classList.add(EDITOR_HOVER_CLASS);
    };

    const handleMouseOut = (event: Event) => {
      const target = event.target as HTMLElement | null;
      target?.classList.remove(EDITOR_HOVER_CLASS);
    };

    const handleInput = () => {
      const selectedElement = selectedElementRef.current;
      if (selectedElement) {
        setSelectedFields(readElementFields(selectedElement));
      }
      syncSourceFromPreview();
    };

    doc.addEventListener('click', handleClick, true);
    doc.addEventListener('mouseover', handleMouseOver, true);
    doc.addEventListener('mouseout', handleMouseOut, true);
    doc.addEventListener('input', handleInput, true);

    cleanupPreviewListenersRef.current = () => {
      doc.removeEventListener('click', handleClick, true);
      doc.removeEventListener('mouseover', handleMouseOver, true);
      doc.removeEventListener('mouseout', handleMouseOut, true);
      doc.removeEventListener('input', handleInput, true);
    };
  }, [clearSelectedElement, getPreviewDocument, injectPreviewStyles, selectElement, syncSourceFromPreview]);

  useEffect(() => () => {
    cleanupPreviewListenersRef.current?.();
  }, []);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setLoadError(tRef.current('htmlVisualEditor.missingId'));
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setDetail(null);
    clearSelectedElement();

    getTextContentDetail(id)
      .then((recordDetail) => {
        if (cancelled) {
          return;
        }
        if (!isHtmlUploadRecord(recordDetail.fileName, recordDetail.contentType || '')) {
          throw new Error(tRef.current('htmlVisualEditor.htmlOnly'));
        }
        const initialContent = recordDetail.content || '<!DOCTYPE html>\n<html><head><meta charset="UTF-8" /></head><body></body></html>';
        setDetail(recordDetail);
        setSourceCode(initialContent);
        setPreviewHtml(initialContent);
        setSourceDirty(false);
        setUploadFileName(buildEditedHtmlFileName(recordDetail.fileName));
      })
      .catch((error) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : tRef.current('htmlVisualEditor.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clearSelectedElement, id]);

  const updateSelectedElement = useCallback((mutator: (element: HTMLElement) => void) => {
    const selectedElement = selectedElementRef.current;
    if (!selectedElement) {
      return;
    }
    mutator(selectedElement);
    selectedElement.classList.add(EDITOR_SELECTED_CLASS);
    setSelectedFields(readElementFields(selectedElement));
    syncSourceFromPreview();
  }, [syncSourceFromPreview]);

  const updateTextContent = (value: string) => {
    updateSelectedElement((element) => {
      element.textContent = value;
      prepareElementForInlineEditing(element);
    });
  };

  const updateClassName = (value: string) => {
    updateSelectedElement((element) => {
      element.className = value;
    });
  };

  const updateAttribute = (attributeName: string, value: string) => {
    updateSelectedElement((element) => {
      setElementAttribute(element, attributeName, value);
    });
  };

  const updateStyleValue = (propertyName: string, value: string) => {
    updateSelectedElement((element) => {
      if (value.trim()) {
        element.style.setProperty(propertyName, value);
      } else {
        element.style.removeProperty(propertyName);
      }
    });
  };

  const updatePixelStyle = (propertyName: string, value: number | string | null) => {
    const numberValue = typeof value === 'number' ? value : Number(value);
    updateSelectedElement((element) => {
      if (Number.isFinite(numberValue)) {
        element.style.setProperty(propertyName, `${numberValue}px`);
        if (propertyName === 'border-width' && numberValue > 0 && !element.style.getPropertyValue('border-style')) {
          element.style.setProperty('border-style', 'solid');
        }
      } else {
        element.style.removeProperty(propertyName);
        if (propertyName === 'border-width') {
          element.style.removeProperty('border-style');
        }
      }
    });
  };

  const updateOpacityStyle = (value: number | string | null) => {
    const numberValue = typeof value === 'number' ? value : Number(value);
    updateStyleValue('opacity', Number.isFinite(numberValue) ? String(numberValue) : '');
  };

  const copySelectedElement = () => {
    const selectedElement = selectedElementRef.current;
    if (!selectedElement || !selectedFields.canMutate) {
      return;
    }
    const clone = selectedElement.cloneNode(true) as HTMLElement;
    cleanEditorMarkers(clone);
    selectedElement.insertAdjacentElement('afterend', clone);
    selectElement(clone);
    syncSourceFromPreview();
  };

  const deleteSelectedElement = () => {
    const selectedElement = selectedElementRef.current;
    if (!selectedElement || !selectedFields.canMutate) {
      return;
    }
    const parent = selectedElement.parentElement;
    selectedElement.remove();
    selectedElementRef.current = null;
    setSelectedFields(EMPTY_SELECTED_FIELDS);
    if (parent && parent.tagName) {
      setSelectedFields(readElementFields(parent));
      selectedElementRef.current = parent;
      parent.classList.add(EDITOR_SELECTED_CLASS);
    }
    syncSourceFromPreview();
  };

  const moveSelectedElement = (direction: 'up' | 'down') => {
    const selectedElement = selectedElementRef.current;
    if (!selectedElement || !selectedFields.canMutate) {
      return;
    }
    if (direction === 'up' && selectedElement.previousElementSibling) {
      selectedElement.parentElement?.insertBefore(selectedElement, selectedElement.previousElementSibling);
    }
    if (direction === 'down' && selectedElement.nextElementSibling) {
      selectedElement.parentElement?.insertBefore(selectedElement.nextElementSibling, selectedElement);
    }
    setSelectedFields(readElementFields(selectedElement));
    syncSourceFromPreview();
  };

  const applySourceToPreview = () => {
    cleanupPreviewListenersRef.current?.();
    clearSelectedElement();
    setPreviewHtml(sourceCode || '<!DOCTYPE html>\n<html><head></head><body></body></html>');
    setSourceDirty(false);
  };

  const getCurrentHtml = () => {
    if (sourceDirty) {
      return sourceCode;
    }
    return syncSourceFromPreview() || sourceCode;
  };

  const handleOverwriteSave = async () => {
    if (!id) {
      return;
    }
    const currentHtml = getCurrentHtml();
    if (getHtmlByteSize(currentHtml) > TEXT_EDIT_MAX_BYTES) {
      message.error(t('htmlVisualEditor.tooLargeForOverwrite'));
      return;
    }

    setSaving(true);
    try {
      const result = await saveTextContent(id, currentHtml);
      message.success(result?.message || t('textEdit.saveSuccess'));
    } catch (error) {
      message.error(error instanceof Error ? error.message : t('textEdit.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleUploadAsNew = async () => {
    const currentHtml = getCurrentHtml();
    const trimmedFileName = uploadFileName.trim();
    if (!currentHtml.trim()) {
      message.warning(t('webHosting.emptyContent'));
      return;
    }
    if (!isValidHtmlFileName(trimmedFileName)) {
      message.warning(t('webHosting.invalidFileName'));
      return;
    }

    setUploading(true);
    try {
      const record = await uploadHtmlContent(
        trimmedFileName,
        currentHtml,
        (percent) => {
          const content = percent === 100
            ? t('upload.uploadComplete')
            : t('upload.uploadProgress', { percent });
          message.info({ key: 'htmlVisualEditorUpload', duration: 1000, content });
        },
        (errorMessage, failedFileName, fileSize, httpStatus) => {
          showUploadErrorModal(t, errorMessage, failedFileName, undefined, fileSize, httpStatus);
        }
      );
      const nextUrl = record?.url || '';
      setNewUploadUrl(nextUrl);
      message.success({ key: 'htmlVisualEditorUpload', content: t('htmlVisualEditor.uploadSuccess') });
    } catch (error) {
      // uploadHtmlContent already shows the detailed modal.
      console.error('上传可视化编辑后的 HTML 失败:', error);
    } finally {
      setUploading(false);
    }
  };

  const copySourceCode = () => {
    copyToClipboard(getCurrentHtml(), t('htmlVisualEditor.sourceCopied'));
  };

  if (loading) {
    return (
      <div className="html-visual-editor-page html-visual-editor-centered">
        <Space direction="vertical" align="center">
          <Spin size="large" />
          <Typography.Text type="secondary">{t('htmlVisualEditor.loading')}</Typography.Text>
        </Space>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="html-visual-editor-page">
        <Result
          status="warning"
          title={t('htmlVisualEditor.loadFailed')}
          subTitle={loadError}
          extra={[
            <Button key="back" icon={<ArrowLeftOutlined />} onClick={() => navigate('/web-hosting')}>
              {t('htmlVisualEditor.backToList')}
            </Button>,
          ]}
        />
      </div>
    );
  }

  const originalUrl = getOriginalUrl(detail);

  return (
    <div className="html-visual-editor-page">
      <div className="html-visual-editor-hero">
        <div>
          <Button type="link" className="html-visual-editor-back" icon={<ArrowLeftOutlined />} onClick={() => navigate('/web-hosting')}>
            {t('htmlVisualEditor.backToList')}
          </Button>
          <Typography.Paragraph ellipsis={{ rows: 1, tooltip: detail?.fileName }}>
            {detail?.fileName}
          </Typography.Paragraph>
        </div>
        <Space wrap>
          {originalUrl && (
            <Button icon={<EyeOutlined />} onClick={() => window.open(originalUrl, '_blank')}>
              {t('htmlVisualEditor.openOriginal')}
            </Button>
          )}
          <Button icon={<CopyOutlined />} onClick={copySourceCode}>
            {t('htmlVisualEditor.copySource')}
          </Button>
          <Button icon={<SaveOutlined />} type="primary" ghost loading={saving} disabled={isOverwriteTooLarge} onClick={handleOverwriteSave}>
            {t('htmlVisualEditor.overwriteSave')}
          </Button>
        </Space>
      </div>

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Alert message={t('htmlVisualEditor.sandboxNotice')} type="info" showIcon />
        {isOverwriteTooLarge && (
          <Alert message={t('htmlVisualEditor.tooLargeForOverwrite')} type="error" showIcon />
        )}
        {hasGoogleFontsResource && (
          <Alert message={t('webHosting.googleFontsWarningTitle')} description={t('webHosting.googleFontsWarningDescription')} type="warning" showIcon />
        )}
        {newUploadUrl && (
          <Alert
            message={t('htmlVisualEditor.newLink')}
            description={(
              <Space wrap>
                <a href={newUploadUrl} target="_blank" rel="noopener noreferrer">{newUploadUrl}</a>
                <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(newUploadUrl, t('common.linkCopied'))}>
                  {t('common.copyLink')}
                </Button>
              </Space>
            )}
            type="success"
            showIcon
          />
        )}
      </Space>

      <Row gutter={[16, 16]} className="html-visual-editor-grid">
        <Col xs={24} xl={11}>
          <Card
            title={(
              <Space>
                <EyeOutlined />
                {t('htmlVisualEditor.preview')}
                <Tag color="blue">iframe</Tag>
              </Space>
            )}
            extra={sourceDirty ? <Tag color="orange">{t('htmlVisualEditor.previewOutdated')}</Tag> : null}
            className="html-visual-editor-card html-visual-editor-preview-card"
            bodyStyle={{ padding: 0 }}
          >
            <iframe
              ref={iframeRef}
              title={t('htmlVisualEditor.preview')}
              className="html-visual-editor-frame"
              srcDoc={previewHtml}
              sandbox="allow-same-origin"
              onLoad={handlePreviewLoad}
            />
          </Card>
        </Col>

        <Col xs={24} xl={6}>
          <Card title={t('htmlVisualEditor.inspector')} className="html-visual-editor-card">
            {selectedFields.tagName ? (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div className="html-visual-editor-selected-title">
                  <Tag color="processing">{selectedFields.tagName}</Tag>
                  <Tooltip title={selectedFields.path}>
                    <Typography.Text ellipsis>{selectedFields.path}</Typography.Text>
                  </Tooltip>
                </div>

                {selectedFields.canEditText && (
                  <div className="html-visual-editor-section">
                    <Typography.Text strong>{t('htmlVisualEditor.contentSection')}</Typography.Text>
                    <Input.TextArea
                      value={selectedFields.textContent}
                      onChange={(event) => updateTextContent(event.target.value)}
                      rows={4}
                      placeholder={t('htmlVisualEditor.textContent')}
                    />
                  </div>
                )}

                {((selectedFields.tagName === 'a' || selectedFields.href)
                  || (['img', 'iframe', 'source', 'video', 'audio'].includes(selectedFields.tagName) || selectedFields.src)
                  || (selectedFields.tagName === 'img' || selectedFields.alt)) && (
                  <div className="html-visual-editor-section">
                    <Typography.Text strong>{t('htmlVisualEditor.linkImageSection')}</Typography.Text>
                    {(selectedFields.tagName === 'a' || selectedFields.href) && (
                      <Input
                        addonBefore={t('htmlVisualEditor.linkUrl')}
                        value={selectedFields.href}
                        onChange={(event) => updateAttribute('href', event.target.value)}
                      />
                    )}
                    {(['img', 'iframe', 'source', 'video', 'audio'].includes(selectedFields.tagName) || selectedFields.src) && (
                      <Input
                        addonBefore={t('htmlVisualEditor.imageUrl')}
                        value={selectedFields.src}
                        onChange={(event) => updateAttribute('src', event.target.value)}
                      />
                    )}
                    {(selectedFields.tagName === 'img' || selectedFields.alt) && (
                      <Input
                        addonBefore={t('htmlVisualEditor.imageDescription')}
                        value={selectedFields.alt}
                        onChange={(event) => updateAttribute('alt', event.target.value)}
                      />
                    )}
                  </div>
                )}

                <div className="html-visual-editor-section">
                  <Typography.Text strong>{t('htmlVisualEditor.appearanceSection')}</Typography.Text>
                  <div className="html-visual-editor-two-columns">
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.textColor')}</Typography.Text>
                      <div className="html-visual-editor-color-row">
                        <input
                          aria-label={t('htmlVisualEditor.textColor')}
                          type="color"
                          value={normalizeHexColor(selectedFields.textColor, '#222222')}
                          onChange={(event) => updateStyleValue('color', event.target.value)}
                        />
                        <Button size="small" onClick={() => updateStyleValue('color', '')}>{t('common.clear')}</Button>
                      </div>
                    </div>
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.backgroundColor')}</Typography.Text>
                      <div className="html-visual-editor-color-row">
                        <input
                          aria-label={t('htmlVisualEditor.backgroundColor')}
                          type="color"
                          value={normalizeHexColor(selectedFields.backgroundColor, '#ffffff')}
                          onChange={(event) => updateStyleValue('background-color', event.target.value)}
                        />
                        <Button size="small" onClick={() => updateStyleValue('background-color', '')}>{t('common.clear')}</Button>
                      </div>
                    </div>
                  </div>
                  <div className="html-visual-editor-two-columns">
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.fontSize')}</Typography.Text>
                      <InputNumber min={1} max={200} value={selectedFields.fontSize} onChange={(value) => updatePixelStyle('font-size', value)} />
                    </div>
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.textAlign')}</Typography.Text>
                      <Segmented
                        block
                        value={selectedFields.textAlign}
                        options={[
                          { label: t('htmlVisualEditor.alignDefault'), value: '' },
                          { label: t('htmlVisualEditor.alignLeft'), value: 'left' },
                          { label: t('htmlVisualEditor.alignCenter'), value: 'center' },
                          { label: t('htmlVisualEditor.alignRight'), value: 'right' },
                        ]}
                        onChange={(value) => updateStyleValue('text-align', String(value))}
                      />
                    </div>
                  </div>
                  <div className="html-visual-editor-two-columns">
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.borderRadius')}</Typography.Text>
                      <InputNumber min={0} max={300} value={selectedFields.borderRadius} onChange={(value) => updatePixelStyle('border-radius', value)} />
                    </div>
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.opacity')}</Typography.Text>
                      <InputNumber min={0} max={1} step={0.1} value={selectedFields.opacity} onChange={updateOpacityStyle} />
                    </div>
                  </div>
                  <div className="html-visual-editor-two-columns">
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.borderWidth')}</Typography.Text>
                      <InputNumber min={0} max={50} value={selectedFields.borderWidth} onChange={(value) => updatePixelStyle('border-width', value)} />
                    </div>
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.borderColor')}</Typography.Text>
                      <div className="html-visual-editor-color-row">
                        <input
                          aria-label={t('htmlVisualEditor.borderColor')}
                          type="color"
                          value={normalizeHexColor(selectedFields.borderColor, '#d9d9d9')}
                          onChange={(event) => updateStyleValue('border-color', event.target.value)}
                        />
                        <Button size="small" onClick={() => updateStyleValue('border-color', '')}>{t('common.clear')}</Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="html-visual-editor-section">
                  <Typography.Text strong>{t('htmlVisualEditor.sizeSpacingSection')}</Typography.Text>
                  <div className="html-visual-editor-two-columns">
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.width')}</Typography.Text>
                      <InputNumber min={0} max={4000} value={selectedFields.width} onChange={(value) => updatePixelStyle('width', value)} />
                    </div>
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.height')}</Typography.Text>
                      <InputNumber min={0} max={4000} value={selectedFields.height} onChange={(value) => updatePixelStyle('height', value)} />
                    </div>
                  </div>
                  <div className="html-visual-editor-two-columns">
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.padding')}</Typography.Text>
                      <InputNumber min={0} max={500} value={selectedFields.padding} onChange={(value) => updatePixelStyle('padding', value)} />
                    </div>
                    <div className="html-visual-editor-field">
                      <Typography.Text type="secondary">{t('htmlVisualEditor.margin')}</Typography.Text>
                      <InputNumber min={0} max={500} value={selectedFields.margin} onChange={(value) => updatePixelStyle('margin', value)} />
                    </div>
                  </div>
                </div>

                <Collapse
                  size="small"
                  ghost
                  items={[{
                    key: 'advanced',
                    label: t('htmlVisualEditor.advancedSettings'),
                    children: (
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Input addonBefore="id" value={selectedFields.id} onChange={(event) => updateAttribute('id', event.target.value)} />
                        <Input addonBefore="class" value={selectedFields.className} onChange={(event) => updateClassName(event.target.value)} />
                        <Input addonBefore="title" value={selectedFields.title} onChange={(event) => updateAttribute('title', event.target.value)} />
                        <Input.TextArea
                          value={selectedFields.style}
                          onChange={(event) => updateAttribute('style', event.target.value)}
                          rows={3}
                          placeholder={t('htmlVisualEditor.inlineStyle')}
                        />
                      </Space>
                    ),
                  }]}
                />

                <Space wrap>
                  <Button icon={<CopyOutlined />} disabled={!selectedFields.canMutate} onClick={copySelectedElement}>
                    {t('htmlVisualEditor.copyElement')}
                  </Button>
                  <Button icon={<ArrowUpOutlined />} disabled={!selectedFields.canMutate} onClick={() => moveSelectedElement('up')}>
                    {t('htmlVisualEditor.moveUp')}
                  </Button>
                  <Button icon={<ArrowDownOutlined />} disabled={!selectedFields.canMutate} onClick={() => moveSelectedElement('down')}>
                    {t('htmlVisualEditor.moveDown')}
                  </Button>
                  <Button danger icon={<DeleteOutlined />} disabled={!selectedFields.canMutate} onClick={deleteSelectedElement}>
                    {t('common.delete')}
                  </Button>
                </Space>
              </Space>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('htmlVisualEditor.noSelection')} />
            )}
          </Card>
        </Col>

        <Col xs={24} xl={7}>
          <Card
            title={(
              <Space>
                <CodeOutlined />
                {t('htmlVisualEditor.source')}
              </Space>
            )}
            className="html-visual-editor-card"
            extra={(
              <Button size="small" icon={<ReloadOutlined />} onClick={applySourceToPreview}>
                {t('htmlVisualEditor.applySource')}
              </Button>
            )}
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Input.TextArea
                value={sourceCode}
                onChange={(event) => {
                  setSourceCode(event.target.value);
                  setSourceDirty(true);
                }}
                onPaste={(event) => event.stopPropagation()}
                rows={24}
                className="html-visual-editor-source"
                placeholder={t('htmlVisualEditor.sourcePlaceholder')}
              />
              <div className="html-visual-editor-publish-row">
                <Typography.Text type={isOverwriteTooLarge ? 'danger' : 'secondary'}>
                  {t('webHosting.contentSize', { count: htmlSize })}
                </Typography.Text>
              </div>
              <Input
                value={uploadFileName}
                onChange={(event) => setUploadFileName(event.target.value)}
                status={uploadFileName && !isValidHtmlFileName(uploadFileName) ? 'error' : undefined}
                addonBefore={t('webHosting.fileName')}
              />
              <Button type="primary" icon={<CloudUploadOutlined />} loading={uploading} block onClick={handleUploadAsNew}>
                {t('htmlVisualEditor.uploadAsNew')}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default WebHostingEditor;
